/**
 * QuickXorHash in the browser: the same fingerprint OneDrive keeps for every file (see apps/api/src/m365/quickXor.ts).
 * Used before an upload to find the same file already in the deal's or project's OneDrive folder, whatever its name.
 */
const WIDTH = 160;
const SHIFT = 11;
const BITS_IN_LAST_CELL = 32;
const MASK64 = (1n << 64n) - 1n;

class QuickXorHash {
  private readonly data = [0n, 0n, 0n];
  private length = 0n;
  private shiftSoFar = 0;

  update(bytes: Uint8Array): void {
    const size = bytes.length;
    let index = Math.floor(this.shiftSoFar / 64);
    let offset = this.shiftSoFar % 64;
    const iterations = Math.min(size, WIDTH);
    for (let i = 0; i < iterations; i++) {
      const isLast = index === this.data.length - 1;
      const bits = isLast ? BITS_IN_LAST_CELL : 64;
      let x = 0;
      for (let j = i; j < size; j += WIDTH) x ^= bytes[j]!;
      const b = BigInt(x);
      if (offset <= bits - 8) {
        this.data[index] = (this.data[index]! ^ (b << BigInt(offset))) & MASK64;
      } else {
        const index2 = isLast ? 0 : index + 1;
        const low = bits - offset;
        this.data[index] = (this.data[index]! ^ (b << BigInt(offset))) & MASK64;
        this.data[index2] = (this.data[index2]! ^ (b >> BigInt(low))) & MASK64;
      }
      offset += SHIFT;
      while (offset >= bits) {
        index = isLast ? 0 : index + 1;
        offset -= bits;
      }
    }
    this.shiftSoFar = (this.shiftSoFar + SHIFT * (size % WIDTH)) % WIDTH;
    this.length += BigInt(size);
  }

  digest(): string {
    const out = new Uint8Array(20);
    const view = new DataView(out.buffer);
    for (let i = 0; i < this.data.length - 1; i++) view.setBigUint64(i * 8, this.data[i]!, true);
    const last = new DataView(new ArrayBuffer(8));
    last.setBigUint64(0, this.data[this.data.length - 1]!, true);
    for (let i = 0; i < 4; i++) out[16 + i] = last.getUint8(i);
    const len = new DataView(new ArrayBuffer(8));
    len.setBigInt64(0, this.length, true);
    for (let i = 0; i < 8; i++) out[12 + i] = out[12 + i]! ^ len.getUint8(i);
    let s = '';
    for (const c of out) s += String.fromCharCode(c);
    return btoa(s);
  }
}

/** The OneDrive fingerprint of a file, read in 4 MB pieces (works for large files too). */
export async function quickXorOf(file: Blob): Promise<string> {
  const h = new QuickXorHash();
  const PIECE = 4 * 1024 * 1024;
  for (let at = 0; at < file.size; at += PIECE) h.update(new Uint8Array(await file.slice(at, at + PIECE).arrayBuffer()));
  return h.digest();
}
